'use client';

import { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { normalizeRole } from '@/lib/auth/roles';

// ─── Explicit Authentication States ──────────────────────────────────────────
// These states make the auth lifecycle deterministic.
// PortalGuard ONLY redirects to /sign-up-login-screen when state === 'UNAUTHENTICATED'.
// It must NEVER redirect during any other state.
export type AuthState =
  | 'AUTH_INITIALISING'      // App just loaded — no determination yet
  | 'SESSION_RESOLVING'      // Checking stored session / waiting for INITIAL_SESSION
  | 'SESSION_FOUND'          // Valid session found — about to resolve role
  | 'ROLE_RESOLVING'         // Session confirmed — fetching role from DB
  | 'AUTHENTICATED'          // Session + role both resolved
  | 'UNAUTHENTICATED'        // Definitively no session — safe to redirect to login
  | 'AUTHENTICATED_ROLE_ERROR'; // Session valid but role resolution failed — DO NOT log out

// ─── Recovery step labels shown in the silent session recovery UI ─────────────
export type RecoveryStep =
  | 'idle' | 'checking' | 'restoring-jwt' | 'restoring-role' | 'restoring-profile' | 'complete' | 'unauthenticated';

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  /** Explicit deterministic auth state — use this for routing decisions */
  authState: AuthState;
  /** Current silent recovery step — used by PortalGuard to show step-by-step status */
  recoveryStep: RecoveryStep;
  /**
   * Role resolved from the production database (member_profiles / office_accounts).
   * The database is the SOLE authoritative source of truth for roles.
   * JWT user_metadata.role is NOT used as an application-role authority.
   * Unknown identities resolve to null — never defaulted to 'member'.
   */
  authRole: string | null;
  /** Username resolved from the production database or JWT metadata. */
  authUsername: string | null;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser]               = useState<any>(null);
  const [session, setSession]         = useState<any>(null);
  const [loading, setLoading]         = useState(true);
  const [authState, setAuthState]     = useState<AuthState>('AUTH_INITIALISING');
  const [authRole, setAuthRole]       = useState<string | null>(null);
  const [authUsername, setAuthUsername] = useState<string | null>(null);
  const [recoveryStep, setRecoveryStep] = useState<RecoveryStep>('checking');

  // ── Single stable Supabase client — NEVER recreate on re-render ──────────────
  const supabase = useMemo(() => createClient(), []);

  // ── Refs for race-condition protection ───────────────────────────────────────
  const resolvedRef              = useRef(false);
  const lastSessionIdRef         = useRef<string | null>(null);
  const dbRoleFetchInFlightRef   = useRef<string | null>(null);
  const silentRecoveryDoneRef    = useRef(false);
  // KEY FIX: pendingNullInitialRef tracks that INITIAL_SESSION:null arrived
  // before silent recovery finished. We must NOT settle as UNAUTHENTICATED
  // until silent recovery confirms there is truly no session.
  const pendingNullInitialRef    = useRef(false);
  const sessionRef               = useRef<any>(null);
  // Track when silent recovery started so we can enforce a minimum wait
  const silentRecoveryStartRef   = useRef<number>(Date.now());

  // ── fetchRoleFromDB ───────────────────────────────────────────────────────────
  const fetchRoleFromDB = useCallback(async (userId: string): Promise<{ role: string | null; username: string | null; source: string } | null> => {
    const attempt = async () => {
      const { data, error } = await supabase.rpc('get_authenticated_user_role');
      if (error) {
        console.warn('[AuthContext] get_authenticated_user_role RPC error:', error.message);
        return null;
      }
      return data as { role: string | null; username: string | null; source: string };
    };

    try {
      const first = await attempt();
      if (first && first.role !== null) return first;
      await new Promise((r) => setTimeout(r, 300));
      return await attempt();
    } catch (err) {
      console.warn('[AuthContext] get_authenticated_user_role exception:', err);
      try {
        await new Promise((r) => setTimeout(r, 300));
        return await attempt();
      } catch (retryErr) {
        console.warn('[AuthContext] get_authenticated_user_role retry exception:', retryErr);
        return null;
      }
    }
  }, [supabase]);

  // ── applySession ─────────────────────────────────────────────────────────────
  // Single source of truth for all auth state updates.
  // CRITICAL RULES:
  //   - Profile loading failures NEVER clear the session or call signOut
  //   - A valid session with a failed role lookup → AUTHENTICATED_ROLE_ERROR (not UNAUTHENTICATED)
  //   - Only a confirmed absence of a valid session → UNAUTHENTICATED
  const applySession = useCallback(async (sess: any, source: string) => {
    const sessionId = sess?.user?.id ?? null;

    // ── FORENSIC TRACE ────────────────────────────────────────────────────────
    console.info(
      `%c[MBG applySession] source="${source}"`,
      'color: #88ccff; font-weight: bold;',
      {
        sessionId,
        hasSession: !!sess,
        resolvedRef: resolvedRef.current,
        lastSessionId: lastSessionIdRef.current,
        dbRoleInFlight: dbRoleFetchInFlightRef.current,
        pendingNullInitial: pendingNullInitialRef.current,
        silentRecoveryDone: silentRecoveryDoneRef.current,
        timestamp: new Date().toISOString(),
      }
    );
    // ── END FORENSIC TRACE ────────────────────────────────────────────────────

    // Skip duplicate non-null session updates already in flight
    if (
      resolvedRef.current &&
      sessionId !== null &&
      sessionId === lastSessionIdRef.current &&
      dbRoleFetchInFlightRef.current === sessionId
    ) {
      return;
    }

    // CRITICAL: Never overwrite a valid session with null unless it's an explicit sign-out.
    // This prevents INITIAL_SESSION:null from destroying a session that is about to arrive.
    const isExplicitSignOut =
      source.includes('SIGNED_OUT') ||
      source.includes('safetyTimeout') ||
      source.includes('noSession');

    if (
      resolvedRef.current &&
      lastSessionIdRef.current !== null &&
      sessionId === null &&
      !isExplicitSignOut
    ) {
      return;
    }

    // ── UNAUTHENTICATED path ──────────────────────────────────────────────────
    // Only reached when there is definitively no session.
    if (!sess || !sess.user) {
      // ── FORENSIC TRACE ──────────────────────────────────────────────────────
      console.warn(
        `%c[MBG applySession → UNAUTHENTICATED] source="${source}"`,
        'color: #ff6666; font-weight: bold;',
        { timestamp: new Date().toISOString() }
      );
      // ── END FORENSIC TRACE ──────────────────────────────────────────────────
      lastSessionIdRef.current = null;
      resolvedRef.current = true;
      dbRoleFetchInFlightRef.current = null;
      sessionRef.current = null;
      setSession(null);
      setUser(null);
      setAuthRole(null);
      setAuthUsername(null);
      setLoading(false);
      setAuthState('UNAUTHENTICATED');
      setRecoveryStep('unauthenticated');
      return;
    }

    // ── AUTHENTICATED path ────────────────────────────────────────────────────
    dbRoleFetchInFlightRef.current = sessionId;
    pendingNullInitialRef.current = false;
    sessionRef.current = sess;

    // Transition: SESSION_FOUND → ROLE_RESOLVING
    setLoading(true);
    setSession(sess);
    setUser(sess.user);
    setAuthState('ROLE_RESOLVING');
    setRecoveryStep('restoring-role');

    // NOTE: JWT user_metadata.role is NOT used as an application-role authority.
    // The DB (get_authenticated_user_role) is the sole authoritative source.
    const jwtUsername = (sess.user.user_metadata?.username as string) || null;

    setRecoveryStep('restoring-profile');
    const dbResult = await fetchRoleFromDB(sessionId);

    // Guard: if a newer session replaced this one while we were fetching, abort.
    if (dbRoleFetchInFlightRef.current !== sessionId) {
      return;
    }

    // Resolve role from DB only — no JWT fallback, no 'member' default.
    // If DB returns null, the identity is unknown (not a member, not office staff).
    const resolvedRole = normalizeRole(dbResult?.role) ?? null;
    const resolvedUsername = dbResult?.username ?? jwtUsername ?? null;

    // ── FORENSIC TRACE ────────────────────────────────────────────────────────
    console.info(
      `%c[MBG applySession → ROLE RESOLVED] source="${source}"`,
      'color: #88ffaa; font-weight: bold;',
      {
        dbRole: dbResult?.role ?? null,
        dbSource: dbResult?.source ?? null,
        resolvedRole,
        resolvedUsername,
        sessionId,
        timestamp: new Date().toISOString(),
      }
    );
    // ── END FORENSIC TRACE ────────────────────────────────────────────────────

    lastSessionIdRef.current = sessionId;
    resolvedRef.current = true;
    dbRoleFetchInFlightRef.current = null;

    if (!resolvedRole) {
      // Session is valid but role resolution failed or identity is unknown.
      // AUTHENTICATED_ROLE_ERROR — DO NOT log out, DO NOT redirect to login.
      // DO NOT default to 'member' — unknown identity must not gain member access.
      console.warn(
        '[AuthContext] Role unresolved after DB lookup — identity unknown. Session preserved. No role assigned.',
        { source, userId: sessionId, dbSource: dbResult?.source ?? null }
      );
      setAuthRole(null);
      setAuthUsername(resolvedUsername);
      setLoading(false);
      setAuthState('AUTHENTICATED_ROLE_ERROR');
      setRecoveryStep('complete');
      return;
    }

    setAuthRole(resolvedRole);
    setAuthUsername(resolvedUsername);
    setLoading(false);
    setAuthState('AUTHENTICATED');
    setRecoveryStep('complete');
  }, [fetchRoleFromDB]);

  // ── Main auth effect ──────────────────────────────────────────────────────────
  useEffect(() => {
    // ─────────────────────────────────────────────────────────────────────────
    // ARCHITECTURE: Explicit auth states with three-path initialization.
    //
    // Path A: onAuthStateChange — authoritative real-time listener.
    // Path B: Silent recovery — concurrent getSession / getUser fallback.
    // Path C: Safety timeout — only forces UNAUTHENTICATED when idle.
    //
    // KEY FIX: INITIAL_SESSION:null is NOT treated as definitive proof that
    // the user is logged out. It is deferred until silent recovery completes.
    // Only after silent recovery confirms no session do we settle as UNAUTHENTICATED.
    // ─────────────────────────────────────────────────────────────────────────

    setAuthState('AUTH_INITIALISING');
    silentRecoveryStartRef.current = Date.now();

    // Path A: Register onAuthStateChange FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'SIGNED_IN' && sess) {
        // Reset all state so applySession always applies this fresh session
        resolvedRef.current = false;
        lastSessionIdRef.current = null;
        dbRoleFetchInFlightRef.current = null;
        pendingNullInitialRef.current = false;
        setLoading(true);
        setAuthState('SESSION_FOUND');
        setRecoveryStep('restoring-role');
        applySession(sess, 'onAuthStateChange:SIGNED_IN');
        return;
      }

      if (event === 'INITIAL_SESSION') {
        if (!sess) {
          // KEY FIX: A null INITIAL_SESSION is NOT definitive proof of logout.
          // In sandboxed/iframe environments, the session cookie may not be
          // readable yet when INITIAL_SESSION fires. Defer the null settle
          // until silent recovery (Path B) has completed its getSession() check.
          if (!resolvedRef.current) {
            if (silentRecoveryDoneRef.current) {
              // Silent recovery already finished with no session — safe to settle
              applySession(null, 'onAuthStateChange:INITIAL_SESSION:null');
            } else {
              // Defer: silent recovery will settle this after getSession() completes
              pendingNullInitialRef.current = true;
              setAuthState('SESSION_RESOLVING');
              setRecoveryStep('checking');
            }
          }
        } else {
          // Session found on page load
          if (!resolvedRef.current || lastSessionIdRef.current !== sess.user?.id) {
            setAuthState('SESSION_FOUND');
            applySession(sess, 'onAuthStateChange:INITIAL_SESSION');
          }
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED' && sess) {
        lastSessionIdRef.current = null;
        dbRoleFetchInFlightRef.current = null;
        applySession(sess, 'onAuthStateChange:TOKEN_REFRESHED');
        return;
      }

      if (event === 'SIGNED_OUT') {
        resolvedRef.current = false;
        lastSessionIdRef.current = null;
        dbRoleFetchInFlightRef.current = null;
        pendingNullInitialRef.current = false;
        applySession(null, 'onAuthStateChange:SIGNED_OUT');
        return;
      }

      // USER_UPDATED and other events
      if (sess) {
        applySession(sess, `onAuthStateChange:${event}`);
      }
    });

    // Path B: Silent recovery — runs concurrently with Path A
    const performSilentRecovery = async () => {
      try {
        setAuthState('SESSION_RESOLVING');
        setRecoveryStep('checking');

        setRecoveryStep('restoring-jwt');
        const { data: { session: storedSession }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.warn('[AuthContext] getSession() error during silent recovery:', sessionError.message);
        }

        if (storedSession?.access_token) {
          // Check if the token needs refresh (expires within 60 seconds)
          const expiresAt = storedSession.expires_at;
          const nowSeconds = Math.floor(Date.now() / 1000);
          const expiresInSeconds = expiresAt ? expiresAt - nowSeconds : Infinity;

          if (expiresInSeconds < 60) {
            const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
            if (!refreshError && refreshed.session) {
              if (!resolvedRef.current || lastSessionIdRef.current === null) {
                resolvedRef.current = false;
                setAuthState('SESSION_FOUND');
                applySession(refreshed.session, 'silentRecovery:refreshed');
              }
              return;
            }
          }

          if (!resolvedRef.current || lastSessionIdRef.current === null) {
            resolvedRef.current = false;
            setAuthState('SESSION_FOUND');
            applySession(storedSession, 'silentRecovery:getSession');
          }
        } else {
          // No stored session — try getUser() as final check
          const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

          if (!userError && currentUser) {
            const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
            if (!refreshError && refreshed.session && (!resolvedRef.current || lastSessionIdRef.current === null)) {
              resolvedRef.current = false;
              setAuthState('SESSION_FOUND');
              applySession(refreshed.session, 'silentRecovery:getUser+refresh');
              return;
            }
          }

          // Truly no session found
          if (!resolvedRef.current) {
            applySession(null, 'silentRecovery:noSession');
          }
        }
      } catch (err) {
        console.error('[AuthContext] Silent recovery error:', err);
        if (!resolvedRef.current) {
          applySession(null, 'silentRecovery:error');
        }
      } finally {
        silentRecoveryDoneRef.current = true;
        // KEY FIX: Only settle as UNAUTHENTICATED if:
        //   1. INITIAL_SESSION:null arrived (pendingNullInitialRef is true)
        //   2. Silent recovery found no session (resolvedRef is false)
        //   3. No session has been applied (lastSessionIdRef is null)
        // This prevents the race where INITIAL_SESSION:null fires before
        // the session cookie is readable in sandboxed environments.
        if (pendingNullInitialRef.current && !resolvedRef.current && lastSessionIdRef.current === null) {
          pendingNullInitialRef.current = false;
          applySession(null, 'onAuthStateChange:INITIAL_SESSION:null');
        }
      }
    };

    performSilentRecovery();

    // Path C: Safety timeout — only force UNAUTHENTICATED when truly idle
    let extendedSafetyTimer: ReturnType<typeof setTimeout> | null = null;
    const safetyTimer = setTimeout(() => {
      if (resolvedRef.current) return;

      // Do not wipe an in-flight authenticated role fetch
      if (dbRoleFetchInFlightRef.current !== null) {
        console.warn('[AuthContext] Safety timeout: role fetch still in flight — extending wait');
        extendedSafetyTimer = setTimeout(() => {
          if (resolvedRef.current) return;
          if (dbRoleFetchInFlightRef.current !== null) {
            // Still stuck — settle as AUTHENTICATED_ROLE_ERROR.
            // DO NOT use JWT metadata as role authority.
            // DO NOT default to 'member'.
            const sess = sessionRef.current;
            if (sess?.user) {
              console.warn('[AuthContext] Safety timeout (extended): DB role fetch timed out — AUTHENTICATED_ROLE_ERROR, no role assigned');
              lastSessionIdRef.current = sess.user.id;
              resolvedRef.current = true;
              dbRoleFetchInFlightRef.current = null;
              setAuthRole(null);
              setAuthUsername((sess.user.user_metadata?.username as string) || null);
              setLoading(false);
              setAuthState('AUTHENTICATED_ROLE_ERROR');
              setRecoveryStep('complete');
              return;
            }
          }
          if (lastSessionIdRef.current === null && !sessionRef.current) {
            console.warn('[AuthContext] Safety timeout (extended): forcing UNAUTHENTICATED');
            applySession(null, 'safetyTimeout');
          }
        }, 10000);
        return;
      }

      // Session present but role never settled — settle as AUTHENTICATED_ROLE_ERROR.
      // DO NOT use JWT metadata as role authority. DO NOT default to 'member'.
      const sess = sessionRef.current;
      if (lastSessionIdRef.current !== null || sess?.user) {
        console.warn('[AuthContext] Safety timeout: session present but DB role timed out — AUTHENTICATED_ROLE_ERROR, no role assigned');
        if (sess?.user) {
          lastSessionIdRef.current = sess.user.id;
          setSession(sess);
          setUser(sess.user);
          setAuthUsername((sess.user.user_metadata?.username as string) || null);
        }
        resolvedRef.current = true;
        dbRoleFetchInFlightRef.current = null;
        setAuthRole(null);
        setLoading(false);
        setAuthState('AUTHENTICATED_ROLE_ERROR');
        setRecoveryStep('complete');
        return;
      }

      console.warn('[AuthContext] Safety timeout: forcing UNAUTHENTICATED after 10s');
      applySession(null, 'safetyTimeout');
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
      if (extendedSafetyTimer) clearTimeout(extendedSafetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth operations ───────────────────────────────────────────────────────────

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          // NOTE: 'role' is intentionally NOT set in JWT metadata.
          // Application roles are resolved exclusively from the DB
          // (office_accounts → member_profiles → null).
          username: metadata?.username || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL ?? (typeof window !== 'undefined' ? window.location.origin : '')}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    resolvedRef.current = false;
    lastSessionIdRef.current = null;
    dbRoleFetchInFlightRef.current = null;
    pendingNullInitialRef.current = false;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user: u }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return u;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // getUserProfile — queries member_profiles (authoritative) only.
  // user_profiles is a legacy table and is NOT used as an authoritative identity source.
  // CRITICAL: A profile loading failure MUST NOT clear the session or call signOut.
  // Authentication and profile loading are separate concerns.
  const getUserProfile = async () => {
    if (!user) return null;

    // Query member_profiles (sole authoritative identity source for members)
    try {
      const { data: memberProfile, error: memberError } = await supabase
        .from('member_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!memberError && memberProfile) {
        return memberProfile;
      }
    } catch (err) {
      // Profile loading failure — DO NOT clear session, DO NOT call signOut
      console.warn('[AuthContext] member_profiles lookup failed (session preserved):', err);
    }

    // Profile not found in member_profiles — return null.
    // DO NOT fall back to user_profiles (legacy table, not authoritative).
    return null;
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    authState,
    recoveryStep,
    authRole,
    authUsername,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
